'use client';

import { useSystemLabels } from '@eliadmin/core/src/hooks/use-system-labels';
import React from 'react';

import type { newsletterSubscribe } from '@eliadmin/core/src/api/newsletter/newsletter-api';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { env } from '@eliadmin/core/src/env';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useMutation } from '@tanstack/react-query';
import { HiOutlineArrowRightCircle } from 'react-icons/hi2';

export function Newsletter() {
  const systemLabels = useSystemLabels();
  const { t } = useTranslations();

  const [email, setEmail] = React.useState('');
  const [isSubscribed, setIsSubscribed] = React.useState(false);

  const subscribeMutation = useMutation({
    mutationFn: async (args: Parameters<typeof newsletterSubscribe>[0]) =>
      (await import('@eliadmin/core/src/api/newsletter/newsletter-api')).newsletterSubscribe(args),
    onSuccess: () => setIsSubscribed(true),
  });

  const locale = useLocaleStore((s) => s.currentLocale);
  const submitLabel = locale === 'sk' ? 'Odoslať prihlásenie na odber' : 'Subscribe to newsletter';

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (isSubscribed || !email || subscribeMutation.isPending) return;
    subscribeMutation.mutate({
      locale,
      body: {
        email,
        gdpr: true,
        return_url: new URL(ROUTES.NEWSLETTER_CONFIRM(), env.NEXT_PUBLIC_APP_URL).toString(),
      },
    });
  }

  return (
    <section id="newsletters" className={cn('w-full', 'ea-homepage-newsletter')}>
      <form className={cn('w-full', 'ea-homepage-newsletter__form')} onSubmit={handleSubmit}>
        <Input
          aria-label={systemLabels('email')}
          id="newsletter-email"
          type="email"
          name="email"
          placeholder="E-mail"
          onChange={(e) => setEmail(e.target.value)}
          className={{
            wrapper: cn('bg-transparent', 'ea-homepage-newsletter__form-item-email-input'),
            main: cn(
              'bg-white/10 text-white/70 placeholder:text-white/50 focus-within:bg-white/15 ',
              'ea-homepage-newsletter__form-item-email-input-main',
            ),
          }}
          after={
            <Button
              type="submit"
              variant={'none'}
              size={'none'}
              className={cn('mr-2', 'ea-homepage-newsletter__form-item-email-submit-button')}
              disabled={email.length === 0 || isSubscribed}
              isLoading={subscribeMutation.isPending}
              aria-label={submitLabel}
            >
              <HiOutlineArrowRightCircle size={24} className="text-white" />
            </Button>
          }
        />
        {isSubscribed && <span className={cn('mt-2 font-medium text-white')}>{t('text3200')}</span>}
      </form>
    </section>
  );
}
